import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';

interface AlumniMember {
  id: string;
  firstName: string;
  lastName: string;
  memberType: 'alumni' | 'responsible_adult';
  category?: 'alumni-mom' | 'alumni-dad' | 'ranch-girl' | 'grandparent' | 'uncle-aunt' | 'family-friend' | 'other';
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  relationshipToFamily?: string;
  textOptIn?: boolean;
  isActingBigSister: boolean;
  isApproved: boolean;
  hasPaperwork: boolean;
  hasBackgroundCheck: boolean;
  hasPaidDues: boolean;
  hasSignedLiability: boolean;
  hasPaidInitialFee: boolean;
  isAlsoResponsibleAdult: boolean;
  isAlsoAlumni: boolean;
  notes?: string;
  dutyRole?: 'none' | 'surrogate_mom' | 'surrogate_dad';
  isActive: boolean;
  userAccount?: {
    id: string;
    email: string;
    isActive: boolean;
    mustChangePassword: boolean;
    currentPassword?: string;
    lastLogin?: string;
  };
}

interface FormData {
  firstName: string;
  lastName: string;
  memberType: 'alumni' | 'responsible_adult';
  category: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  relationshipToFamily: string;
  textOptIn: boolean;
  isActingBigSister: boolean;
  isApproved: boolean;
  hasPaperwork: boolean;
  hasBackgroundCheck: boolean;
  hasPaidDues: boolean;
  hasSignedLiability: boolean;
  hasPaidInitialFee: boolean;
  isAlsoResponsibleAdult: boolean;
  isAlsoAlumni: boolean;
  dutyRole: string;
  notes: string;
}

const ALUMNI_CATEGORIES = {
  'alumni-mom': 'Alumni Mom',
  'alumni-dad': 'Alumni Dad',
  'ranch-girl': 'Ranch Girl'
};

const RESPONSIBLE_ADULT_CATEGORIES = {
  'grandparent': 'Grandparent',
  'uncle-aunt': 'Uncle/Aunt',
  'family-friend': 'Family Friend',
  'other': 'Other'
};

const ALL_CATEGORY_LABELS = {
  ...ALUMNI_CATEGORIES,
  ...RESPONSIBLE_ADULT_CATEGORIES
};

type MemberTypeFilter = 'all' | 'alumni' | 'responsible_adult';

export default function AlumniManagement() {
  const [members, setMembers] = useState<AlumniMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState<AlumniMember | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<AlumniMember | null>(null);
  const [userPassword, setUserPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  
  // Filter and selection state
  const [memberTypeFilter, setMemberTypeFilter] = useState<MemberTypeFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    memberType: 'alumni',
    category: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'MO',
    zipCode: '',
    relationshipToFamily: '',
    textOptIn: false,
    isActingBigSister: false,
    isApproved: false,
    hasPaperwork: false,
    hasBackgroundCheck: false,
    hasPaidDues: false,
    hasSignedLiability: false,
    hasPaidInitialFee: false,
    isAlsoResponsibleAdult: false,
    isAlsoAlumni: false,
    dutyRole: 'none',
    notes: ''
  });

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {})
    };

    const response = await fetch(url, {
      ...options,
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  // Filter members based on selected tab
  const filteredMembers = members.filter(member => {
    if (memberTypeFilter === 'all') return true;
    return member.memberType === memberTypeFilter;
  });

  const exportToCSV = () => {
    const headers = [
      'Member Type',
      'Also Resp. Adult',
      'Also Alumni',
      'First Name',
      'Last Name',
      'Category',
      'Email',
      'Phone',
      'Address',
      'City',
      'State',
      'Zip',
      'Relationship',
      'Approved',
      'Paperwork',
      'Background Check',
      'Paid Dues',
      'Acting Big Sister',
      'Has User Account',
      'Notes',
      'Active'
    ];

    const rows = filteredMembers.map(member => [
      member.memberType === 'alumni' ? 'Alumni' : 'Responsible Adult',
      member.isAlsoResponsibleAdult ? 'Yes' : 'No',
      member.isAlsoAlumni ? 'Yes' : 'No',
      member.firstName,
      member.lastName,
      member.category ? ALL_CATEGORY_LABELS[member.category] || member.category : '',
      member.email || '',
      member.phone || '',
      member.address || '',
      member.city || '',
      member.state || '',
      member.zipCode || '',
      member.relationshipToFamily || '',
      member.isApproved ? 'Yes' : 'No',
      member.hasPaperwork ? 'Yes' : 'No',
      member.hasBackgroundCheck ? 'Yes' : 'No',
      member.hasPaidDues ? 'Yes' : 'No',
      member.isActingBigSister ? 'Yes' : 'No',
      member.userAccount ? 'Yes' : 'No',
      member.notes || '',
      member.isActive ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        const cellStr = String(cell).replace(/"/g, '""');
        return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
          ? `"${cellStr}"` 
          : cellStr;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `associated_members_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await authenticatedFetch('/api/alumni');
      if (data.success) {
        const membersWithUsers = await Promise.all(
          data.data.map(async (member: AlumniMember) => {
            try {
              const userResponse = await authenticatedFetch(`/api/alumni/${member.id}/user`);
              return {
                ...member,
                userAccount: userResponse.success ? userResponse.data : null
              };
            } catch (error) {
              return member;
            }
          })
        );
        setMembers(membersWithUsers);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      alert('Error loading associated members');
    } finally {
      setLoading(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleManageUser = (member: AlumniMember) => {
    setSelectedMember(member);
    if (member.userAccount) {
      setUserPassword('');
      setGeneratedPassword('');
    } else {
      const newPassword = generateRandomPassword();
      setUserPassword(newPassword);
      setGeneratedPassword(newPassword);
    }
    setShowUserModal(true);
  };

  const handleCreateUserAccount = async () => {
    if (!selectedMember || !userPassword || userPassword.length < 6) {
      alert('Please enter a password with at least 6 characters');
      return;
    }

    try {
      const data = await authenticatedFetch(`/api/alumni/${selectedMember.id}/create-user`, {
        method: 'POST',
        body: JSON.stringify({
          password: userPassword,
          mustChangePassword: true
        })
      });

      if (data.success) {
        alert(`User account created successfully!\n\nEmail: ${selectedMember.email}\nTemporary Password: ${data.user.temporaryPassword}\n\nPlease share these credentials with the member.`);
        setShowUserModal(false);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error creating user account:', error);
      alert('Error creating user account. Please try again.');
    }
  };

  const handleUpdatePassword = async () => {
    if (!selectedMember || !userPassword || userPassword.length < 6) {
      alert('Please enter a password with at least 6 characters');
      return;
    }

    try {
      const data = await authenticatedFetch(`/api/alumni/${selectedMember.id}/user-password`, {
        method: 'PUT',
        body: JSON.stringify({
          newPassword: userPassword,
          mustChangePassword: true
        })
      });

      if (data.success) {
        alert(`Password updated successfully!\n\nNew Temporary Password: ${data.newPassword}\n\nPlease share this with the member.`);
        setShowUserModal(false);
        fetchMembers();
      }
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Error updating password. Please try again.');
    }
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectedIds.size === filteredMembers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMembers.map(m => m.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  // Bulk update handler
  const handleBulkUpdate = async (field: string, value: boolean) => {
    if (selectedIds.size === 0) {
      alert('Please select at least one member');
      return;
    }

    setBulkUpdating(true);
    try {
      const response = await authenticatedFetch('/api/alumni/bulk-update-status', {
        method: 'PUT',
        body: JSON.stringify({
          memberIds: Array.from(selectedIds),
          field,
          value
        })
      });

      if (response.success) {
        // Update local state
        setMembers(prev => prev.map(m => 
          selectedIds.has(m.id) ? { ...m, [field]: value } : m
        ));
        setSelectedIds(new Set());
      }
    } catch (error) {
      console.error('Error bulk updating:', error);
      alert('Error updating members');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Individual checkbox update
  const handleCheckboxChange = async (memberId: string, field: string, value: boolean) => {
    try {
      await authenticatedFetch(`/api/alumni/${memberId}`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: value })
      });
      
      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, [field]: value } : m
      ));
    } catch (error) {
      console.error('Error updating checkbox:', error);
      alert('Error updating member');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      memberType: 'alumni',
      category: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: 'MO',
      zipCode: '',
      relationshipToFamily: '',
      textOptIn: false,
      isActingBigSister: false,
      isApproved: false,
      hasPaperwork: false,
      hasBackgroundCheck: false,
      hasPaidDues: false,
      hasSignedLiability: false,
      hasPaidInitialFee: false,
      isAlsoResponsibleAdult: false,
      isAlsoAlumni: false,
      dutyRole: 'none',
      notes: ''
    });
    setEditingMember(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (editingMember) {
        await authenticatedFetch(`/api/alumni/${editingMember.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        alert('Member updated successfully');
      } else {
        await authenticatedFetch('/api/alumni', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        alert('Member created successfully');
      }

      setShowAddModal(false);
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      alert('Error saving member. Please try again.');
    }
  };

  const handleEdit = (member: AlumniMember) => {
    setEditingMember(member);
    setFormData({
      firstName: member.firstName,
      lastName: member.lastName,
      memberType: member.memberType || 'alumni',
      category: member.category || '',
      email: member.email || '',
      phone: member.phone || '',
      address: member.address || '',
      city: member.city || '',
      state: member.state || 'MO',
      zipCode: member.zipCode || '',
      relationshipToFamily: member.relationshipToFamily || '',
      textOptIn: member.textOptIn || false,
      isActingBigSister: member.isActingBigSister,
      isApproved: member.isApproved || false,
      hasPaperwork: member.hasPaperwork || false,
      hasBackgroundCheck: member.hasBackgroundCheck,
      hasPaidDues: member.hasPaidDues,
      hasSignedLiability: member.hasSignedLiability,
      hasPaidInitialFee: member.hasPaidInitialFee,
      isAlsoResponsibleAdult: member.isAlsoResponsibleAdult || false,
      isAlsoAlumni: member.isAlsoAlumni || false,
      dutyRole: member.dutyRole || 'none',
      notes: member.notes || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (member: AlumniMember) => {
    if (!window.confirm(`Are you sure you want to deactivate ${member.firstName} ${member.lastName}?`)) {
      return;
    }

    try {
      await authenticatedFetch(`/api/alumni/${member.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: false })
      });
      alert('Member deactivated successfully');
      fetchMembers();
    } catch (error) {
      console.error('Error deactivating member:', error);
      alert('Error deactivating member. Please try again.');
    }
  };

  const handleImpersonate = async (member: AlumniMember) => {
    if (!member.userAccount) {
      alert('This member does not have a user account');
      return;
    }

    if (!window.confirm(`Impersonate ${member.firstName} ${member.lastName}? You will be logged in as this user.`)) {
      return;
    }

    try {
      await startImpersonation(member.userAccount.id);
      navigate('/alumni/dashboard');
    } catch (error) {
      console.error('Error impersonating user:', error);
      alert('Error impersonating user');
    }
  };

  const getCategoryOptions = () => {
    if (formData.memberType === 'alumni' && !formData.isAlsoResponsibleAdult) {
      return ALUMNI_CATEGORIES;
    }
    if (formData.memberType === 'responsible_adult' && !formData.isAlsoAlumni) {
      return RESPONSIBLE_ADULT_CATEGORIES;
    }
    return { ...ALUMNI_CATEGORIES, ...RESPONSIBLE_ADULT_CATEGORIES };
  };

  const showDutyRoleField =
    formData.memberType === 'responsible_adult' ||
    formData.isAlsoResponsibleAdult ||
    formData.category !== 'ranch-girl';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading associated members...</div>
      </div>
    );
  }

  const alumniCount = members.filter(m => m.memberType === 'alumni' || !m.memberType).length;
  const responsibleAdultCount = members.filter(m => m.memberType === 'responsible_adult').length;

  return (
    <div className="p-6 max-w-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Associated Members</h1>
          <p className="text-gray-600 mt-1">
            Manage alumni members and responsible adults
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
          >
            📥 Export CSV
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-ggaa-green text-white px-4 py-2 rounded-lg hover:bg-ggaa-dark"
          >
            + Add Member
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          onClick={() => { setMemberTypeFilter('all'); setSelectedIds(new Set()); }}
          className={`px-4 py-2 font-medium ${memberTypeFilter === 'all' 
            ? 'border-b-2 border-ggaa-green text-ggaa-green' 
            : 'text-gray-500 hover:text-gray-700'}`}
        >
          All ({members.length})
        </button>
        <button
          onClick={() => { setMemberTypeFilter('alumni'); setSelectedIds(new Set()); }}
          className={`px-4 py-2 font-medium ${memberTypeFilter === 'alumni' 
            ? 'border-b-2 border-ggaa-green text-ggaa-green' 
            : 'text-gray-500 hover:text-gray-700'}`}
        >
          Alumni ({alumniCount})
        </button>
        <button
          onClick={() => { setMemberTypeFilter('responsible_adult'); setSelectedIds(new Set()); }}
          className={`px-4 py-2 font-medium ${memberTypeFilter === 'responsible_adult' 
            ? 'border-b-2 border-ggaa-green text-ggaa-green' 
            : 'text-gray-500 hover:text-gray-700'}`}
        >
          Responsible Adults ({responsibleAdultCount})
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center justify-between">
          <span className="text-blue-800 font-medium">
            {selectedIds.size} member(s) selected
          </span>
          <div className="flex gap-2">
            <span className="text-gray-600 mr-2">Set all to:</span>
            <button
              onClick={() => handleBulkUpdate('isApproved', true)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200 text-sm"
            >
              ✓ Approved
            </button>
            <button
              onClick={() => handleBulkUpdate('isApproved', false)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
            >
              ✗ Approved
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => handleBulkUpdate('hasPaperwork', true)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200 text-sm"
            >
              ✓ Paperwork
            </button>
            <button
              onClick={() => handleBulkUpdate('hasPaperwork', false)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
            >
              ✗ Paperwork
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => handleBulkUpdate('hasBackgroundCheck', true)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200 text-sm"
            >
              ✓ BG Check
            </button>
            <button
              onClick={() => handleBulkUpdate('hasBackgroundCheck', false)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
            >
              ✗ BG Check
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => handleBulkUpdate('hasPaidDues', true)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-green-100 text-green-800 rounded hover:bg-green-200 text-sm"
            >
              ✓ Dues
            </button>
            <button
              onClick={() => handleBulkUpdate('hasPaidDues', false)}
              disabled={bulkUpdating}
              className="px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
            >
              ✗ Dues
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm ml-2"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filteredMembers.length && filteredMembers.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase" title="Approved">✓</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase" title="Paperwork">📄</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase" title="Background Check">🔍</th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase" title="Paid Dues">💰</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User Account</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMembers.map((member) => (
              <tr key={member.id} className={selectedIds.has(member.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                <td className="px-3 py-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(member.id)}
                    onChange={() => handleSelectOne(member.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {member.firstName} {member.lastName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {member.category ? ALL_CATEGORY_LABELS[member.category] || member.category : '—'}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    <span className={`px-2 py-1 text-xs rounded-full inline-block w-fit ${
                      member.memberType === 'responsible_adult' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {member.memberType === 'responsible_adult' ? 'Resp. Adult' : 'Alumni'}
                    </span>
                    {member.memberType === 'alumni' && member.isAlsoResponsibleAdult && (
                      <span className="px-2 py-1 text-xs rounded-full bg-purple-50 text-purple-600 border border-purple-200 inline-block w-fit">
                        + Resp. Adult
                      </span>
                    )}
                    {member.memberType === 'responsible_adult' && member.isAlsoAlumni && (
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-600 border border-blue-200 inline-block w-fit">
                        + Alumni
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="text-sm text-gray-900">{member.email}</div>
                  <div className="text-xs text-gray-500">{member.phone || '—'}</div>
                </td>
                <td className="px-3 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={member.isApproved}
                    onChange={(e) => handleCheckboxChange(member.id, 'isApproved', e.target.checked)}
                    className="rounded border-gray-300 text-green-600 cursor-pointer"
                    title="Approved"
                  />
                </td>
                <td className="px-3 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={member.hasPaperwork}
                    onChange={(e) => handleCheckboxChange(member.id, 'hasPaperwork', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 cursor-pointer"
                    title="Has Paperwork"
                  />
                </td>
                <td className="px-3 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={member.hasBackgroundCheck}
                    onChange={(e) => handleCheckboxChange(member.id, 'hasBackgroundCheck', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 cursor-pointer"
                    title="Background Check"
                  />
                </td>
                <td className="px-3 py-4 text-center">
                  <input
                    type="checkbox"
                    checked={member.hasPaidDues}
                    onChange={(e) => handleCheckboxChange(member.id, 'hasPaidDues', e.target.checked)}
                    className="rounded border-gray-300 text-yellow-600 cursor-pointer"
                    title="Paid Dues"
                  />
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {member.userAccount ? (
                    <span className="text-green-600 text-sm">✓ Has Account</span>
                  ) : (
                    <span className="text-gray-400 text-sm">No Account</span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm space-x-2">
                  <button
                    onClick={() => handleEdit(member)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleManageUser(member)}
                    className="text-purple-600 hover:text-purple-800"
                  >
                    User
                  </button>
                  {member.userAccount && (
                    <button
                      onClick={() => handleImpersonate(member)}
                      className="text-orange-600 hover:text-orange-800"
                    >
                      👤
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(member)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredMembers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No {memberTypeFilter === 'all' ? '' : memberTypeFilter === 'alumni' ? 'alumni ' : 'responsible adult '}members found
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingMember ? 'Edit Member' : 'Add New Member'}
            </h2>
            <form onSubmit={handleSubmit}>
              {/* Member Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Member Type *</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="memberType"
                      value="alumni"
                      checked={formData.memberType === 'alumni'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Alumni
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="memberType"
                      value="responsible_adult"
                      checked={formData.memberType === 'responsible_adult'}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    Responsible Adult
                  </label>
                </div>
                {/* Dual Type Checkbox */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  {formData.memberType === 'alumni' ? (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isAlsoResponsibleAdult"
                        checked={formData.isAlsoResponsibleAdult}
                        onChange={handleInputChange}
                        className="mr-2 rounded border-gray-300 text-purple-600"
                      />
                      <span className="text-sm text-gray-700">Also a Responsible Adult</span>
                      <span className="ml-2 text-xs text-gray-500">(Can pick up ranch girls)</span>
                    </label>
                  ) : (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isAlsoAlumni"
                        checked={formData.isAlsoAlumni}
                        onChange={handleInputChange}
                        className="mr-2 rounded border-gray-300 text-blue-600"
                      />
                      <span className="text-sm text-gray-700">Also an Alumni</span>
                      <span className="ml-2 text-xs text-gray-500">(Former ranch girl parent/member)</span>
                    </label>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Select Category</option>
                    {Object.entries(getCategoryOptions()).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                {formData.memberType === 'responsible_adult' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relationship to Family</label>
                    <input
                      type="text"
                      name="relationshipToFamily"
                      value={formData.relationshipToFamily}
                      onChange={handleInputChange}
                      placeholder="e.g., Grandmother of Sarah Johnson"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                )}
              </div>

              {showDutyRoleField && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duty Parent Role</label>
                  <select
                    name="dutyRole"
                    value={formData.dutyRole}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="none">None</option>
                    <option value="surrogate_mom">Surrogate Mom</option>
                    <option value="surrogate_dad">Surrogate Dad</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Surrogate parents can cover Saturday duty when troop parents are unavailable.
                  </p>
                </div>
              )}

              {/* Address Fields (for responsible adults) */}
              {formData.memberType === 'responsible_adult' && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Address</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Zip</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Checkboxes */}
              <div className="mt-4 pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isApproved"
                      checked={formData.isApproved}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300"
                    />
                    <span className="text-sm">Approved</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasPaperwork"
                      checked={formData.hasPaperwork}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300"
                    />
                    <span className="text-sm">Has Paperwork</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasBackgroundCheck"
                      checked={formData.hasBackgroundCheck}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300"
                    />
                    <span className="text-sm">Background Check</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasPaidDues"
                      checked={formData.hasPaidDues}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300"
                    />
                    <span className="text-sm">Paid Dues</span>
                  </label>
                  {formData.memberType === 'alumni' && (
                    <>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActingBigSister"
                          checked={formData.isActingBigSister}
                          onChange={handleInputChange}
                          className="mr-2 rounded border-gray-300"
                        />
                        <span className="text-sm">Acting Big Sister</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          name="hasPaidInitialFee"
                          checked={formData.hasPaidInitialFee}
                          onChange={handleInputChange}
                          className="mr-2 rounded border-gray-300"
                        />
                        <span className="text-sm">Paid Initial Fee</span>
                      </label>
                    </>
                  )}
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="textOptIn"
                      checked={formData.textOptIn}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300"
                    />
                    <span className="text-sm">Text Opt-In</span>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark"
                >
                  {editingMember ? 'Update Member' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Account Modal */}
      {showUserModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">
              {selectedMember.userAccount ? 'Manage User Account' : 'Create User Account'}
            </h2>
            <p className="text-gray-600 mb-4">
              {selectedMember.firstName} {selectedMember.lastName}
              <br />
              <span className="text-sm">{selectedMember.email}</span>
            </p>

            {selectedMember.userAccount ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 text-sm">
                    ✓ User account exists
                    <br />
                    Last login: {selectedMember.userAccount.lastLogin 
                      ? new Date(selectedMember.userAccount.lastLogin).toLocaleDateString() 
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userPassword}
                      onChange={(e) => setUserPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const pwd = generateRandomPassword();
                        setUserPassword(pwd);
                        setGeneratedPassword(pwd);
                      }}
                      className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      Generate
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleUpdatePassword}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Update Password
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Initial Password</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userPassword}
                      onChange={(e) => setUserPassword(e.target.value)}
                      placeholder="Enter password"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const pwd = generateRandomPassword();
                        setUserPassword(pwd);
                        setGeneratedPassword(pwd);
                      }}
                      className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      Generate
                    </button>
                  </div>
                  {generatedPassword && (
                    <p className="text-xs text-gray-500 mt-1">
                      Generated: <code className="bg-gray-100 px-1">{generatedPassword}</code>
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCreateUserAccount}
                  className="w-full px-4 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-dark"
                >
                  Create User Account
                </button>
              </div>
            )}

            <button
              onClick={() => {
                setShowUserModal(false);
                setSelectedMember(null);
                setUserPassword('');
                setGeneratedPassword('');
              }}
              className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
